/**
 * Queue filter model + filter→query mapping (§18 /queue filters, §17 GET /orders).
 * Pure, DOM-free so it's unit-testable in the node vitest project.
 *
 * §A — every predicate except the date range is now MULTI-VALUE: the worker's
 * GET /orders / GET /orders/counts accept repeated/comma-joined params and map
 * them to `in` (tags → `hasSome`). `status` is array-capable too (the count tabs
 * drive it as a 0/1-length selection; saved views may carry several). Saved
 * views (§21) persist exactly this shape (minus pagination).
 */

/** The user-facing queue filter. All fields optional; empty = "all open". */
export interface QueueFilter {
  /** OrderStatus strings; [] / undefined = "all" (driven by the count tabs). */
  status?: string[];
  /** Store regions (EU/US/CA) or row cuids — the worker accepts either. */
  storeId?: string[];
  carrierCode?: string[];
  /** ISO-2 destination countries; upper-cased on the wire. */
  destinationCountry?: string[];
  /** Shopify-parity: financial status (paid/pending/refunded/…). */
  financialStatus?: string[];
  /** Shopify-parity: payment gateway/method name. */
  paymentGateway?: string[];
  /** Shopify-parity: shipping method title (exact). */
  shippingMethod?: string[];
  /** Shopify-parity: order tags (matches ANY selected tag). */
  tags?: string[];
  /** Created-date range (Shopify timestamp), YYYY-MM-DD; either bound optional. */
  createdFrom?: string;
  createdTo?: string;
  /** Task 1 free-text search (order # · customer · destination · SKU · tracking #).
   *  Transient — narrows the result set but is NOT part of a saved view. */
  search?: string;
  /** Task 2 active sort column (worker `sort` key); absent = express-first default. */
  sort?: string;
  /** Task 2 sort direction; defaults to asc when a sort is set. */
  dir?: "asc" | "desc";
  /** 1-based page index (§17 offset pagination). */
  page?: number;
  /** Page size; worker clamps to its MAX_LIMIT. */
  limit?: number;
}

/** The filter fields that define a saved view (everything except pagination). */
export type ViewFilter = Omit<QueueFilter, "page" | "limit">;

/** The multi-value predicate keys, in a stable order (drives query + equality). */
export const MULTI_KEYS = [
  "status",
  "storeId",
  "carrierCode",
  "destinationCountry",
  "financialStatus",
  "paymentGateway",
  "shippingMethod",
  "tags",
] as const satisfies ReadonlyArray<keyof ViewFilter>;

type MultiKey = (typeof MULTI_KEYS)[number];

/** Normalise a maybe-array predicate to a clean string[] (trim, drop blanks, dedup). */
function clean(values: string[] | undefined, upper = false): string[] {
  if (!values) return [];
  const out: string[] = [];
  for (const v of values) {
    const t = (upper ? v.toUpperCase() : v).trim();
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}

/**
 * Map a QueueFilter to URLSearchParams for GET /orders and GET /orders/counts.
 * Multi-value predicates are appended as repeated params; country is upper-cased
 * to match the worker compare. Empty predicates are omitted entirely.
 */
export function filterToQuery(filter: QueueFilter): URLSearchParams {
  const qs = new URLSearchParams();
  for (const key of MULTI_KEYS) {
    for (const v of clean(filter[key], key === "destinationCountry")) qs.append(key, v);
  }
  if (filter.createdFrom) qs.set("createdFrom", filter.createdFrom);
  if (filter.createdTo) qs.set("createdTo", filter.createdTo);
  if (filter.search && filter.search.trim()) qs.set("q", filter.search.trim());
  if (filter.sort) {
    qs.set("sort", filter.sort);
    qs.set("dir", filter.dir === "desc" ? "desc" : "asc");
  }
  if (filter.page != null && filter.page > 0) qs.set("page", String(filter.page));
  if (filter.limit != null && filter.limit > 0) qs.set("limit", String(filter.limit));
  return qs;
}

/** Strip pagination so two filters compare equal iff their predicates match. */
export function toViewFilter(filter: QueueFilter): ViewFilter {
  const out: ViewFilter = {};
  for (const key of MULTI_KEYS) {
    const v = clean(filter[key], key === "destinationCountry");
    if (v.length) out[key] = v;
  }
  if (filter.createdFrom) out.createdFrom = filter.createdFrom;
  if (filter.createdTo) out.createdTo = filter.createdTo;
  return out;
}

/** Order-insensitive set equality over a single multi-value predicate. */
function sameSet(a: string[] | undefined, b: string[] | undefined): boolean {
  const x = clean(a);
  const y = clean(b);
  if (x.length !== y.length) return false;
  const set = new Set(x.map((s) => s.toUpperCase()));
  return y.every((s) => set.has(s.toUpperCase()));
}

/** Stable equality over the predicate fields (used to highlight the active view). */
export function sameViewFilter(a: ViewFilter, b: ViewFilter): boolean {
  for (const key of MULTI_KEYS) {
    if (!sameSet(a[key], b[key])) return false;
  }
  return (a.createdFrom ?? "") === (b.createdFrom ?? "") && (a.createdTo ?? "") === (b.createdTo ?? "");
}

/** True when a view filter carries no predicates (the implicit "all open" view). */
export function isEmptyViewFilter(f: ViewFilter): boolean {
  for (const key of MULTI_KEYS) if (clean(f[key]).length) return false;
  return !f.createdFrom && !f.createdTo;
}

/** Count the active predicate groups (for a "Filters · N" affordance). */
export function activeFilterCount(f: ViewFilter): number {
  let n = 0;
  for (const key of MULTI_KEYS) if (clean(f[key]).length) n += 1;
  if (f.createdFrom || f.createdTo) n += 1;
  return n;
}

export type { MultiKey };
